/**
 * Route Plan Service - Fleet Premium
 * Servico de persistencia de planos de rota
 *
 * Endpoints REST API:
 * - /api/route-plans - CRUD de planos
 * - /api/route-plans/{id}/stops - Paradas do plano
 * - /api/route-plans/{id}/vehicles - Veiculos do plano
 * - /api/route-plans/{id}/events - Eventos do plano
 * - /api/route-plan-stops - Operacoes especializadas em paradas
 * - /api/route-plan-vehicles - Operacoes especializadas em veiculos
 */

import fetchOrThrow from '../util/fetchOrThrow';

// ============================================================================
// ROUTE PLANS - CRUD Principal
// ============================================================================

export const getRoutePlans = async (params = {}) => {
  const searchParams = new URLSearchParams();
  searchParams.set('all', params.all !== false ? 'true' : 'false');
  if (params.status) searchParams.set('status', params.status);
  if (params.from) searchParams.set('from', params.from);
  if (params.to) searchParams.set('to', params.to);

  const query = searchParams.toString();
  const url = `/api/route-plans${query ? `?${query}` : ''}`;
  const response = await fetchOrThrow(url);
  return response.json();
};

export const getRoutePlan = async (id) => {
  const response = await fetchOrThrow(`/api/route-plans/${id}`);
  return response.json();
};

export const createRoutePlan = async (plan) => {
  const response = await fetchOrThrow('/api/route-plans', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(plan),
  });
  return response.json();
};

export const updateRoutePlan = async (id, plan) => {
  const response = await fetchOrThrow(`/api/route-plans/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...plan, id }),
  });
  return response.json();
};

export const deleteRoutePlan = async (id) => {
  await fetchOrThrow(`/api/route-plans/${id}`, { method: 'DELETE' });
};

export const getRoutePlanSummary = async (id) => {
  const response = await fetchOrThrow(`/api/route-plans/${id}/summary`);
  return response.json();
};

export const updateRoutePlanStatus = async (id, status) => {
  const response = await fetchOrThrow(`/api/route-plans/${id}/status?status=${status}`, {
    method: 'PUT',
  });
  return response.json();
};

// ============================================================================
// STOPS - Paradas do plano
// ============================================================================

export const getRoutePlanStops = async (routePlanId) => {
  const response = await fetchOrThrow(`/api/route-plans/${routePlanId}/stops`);
  return response.json();
};

export const addRoutePlanStop = async (routePlanId, stop) => {
  const response = await fetchOrThrow(`/api/route-plans/${routePlanId}/stops`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(stop),
  });
  return response.json();
};

export const updateRoutePlanStop = async (routePlanId, stopId, stop) => {
  const response = await fetchOrThrow(`/api/route-plans/${routePlanId}/stops/${stopId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(stop),
  });
  return response.json();
};

export const removeRoutePlanStop = async (routePlanId, stopId) => {
  await fetchOrThrow(`/api/route-plans/${routePlanId}/stops/${stopId}`, { method: 'DELETE' });
};

export const updateStopStatus = async (stopId, status) => {
  const response = await fetchOrThrow(`/api/route-plan-stops/${stopId}/status?status=${status}`, {
    method: 'PUT',
  });
  return response.json();
};

export const completeStop = async (stopId, data = {}) => {
  const response = await fetchOrThrow(`/api/route-plan-stops/${stopId}/complete`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  return response.json();
};

export const skipStop = async (stopId, reason = '') => {
  const response = await fetchOrThrow(`/api/route-plan-stops/${stopId}/skip?reason=${encodeURIComponent(reason)}`, {
    method: 'PUT',
  });
  return response.json();
};

// ============================================================================
// VEHICLES - Veiculos do plano
// ============================================================================

export const getRoutePlanVehicles = async (routePlanId) => {
  const response = await fetchOrThrow(`/api/route-plans/${routePlanId}/vehicles`);
  return response.json();
};

export const addRoutePlanVehicle = async (routePlanId, vehicle) => {
  const response = await fetchOrThrow(`/api/route-plans/${routePlanId}/vehicles`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(vehicle),
  });
  return response.json();
};

export const updateRoutePlanVehicle = async (routePlanId, vehicleId, vehicle) => {
  const response = await fetchOrThrow(`/api/route-plans/${routePlanId}/vehicles/${vehicleId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(vehicle),
  });
  return response.json();
};

export const removeRoutePlanVehicle = async (routePlanId, vehicleId) => {
  await fetchOrThrow(`/api/route-plans/${routePlanId}/vehicles/${vehicleId}`, { method: 'DELETE' });
};

export const startVehicle = async (vehicleId) => {
  const response = await fetchOrThrow(`/api/route-plan-vehicles/${vehicleId}/start`, {
    method: 'PUT',
  });
  return response.json();
};

export const completeVehicle = async (vehicleId, data = {}) => {
  const response = await fetchOrThrow(`/api/route-plan-vehicles/${vehicleId}/complete`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  return response.json();
};

export const getVehicleProgress = async (vehicleId) => {
  const response = await fetchOrThrow(`/api/route-plan-vehicles/${vehicleId}/progress`);
  return response.json();
};

// ============================================================================
// EVENTS - Eventos do plano
// ============================================================================

export const getRoutePlanEvents = async (routePlanId, severity = null) => {
  const url = severity
    ? `/api/route-plans/${routePlanId}/events?severity=${severity}`
    : `/api/route-plans/${routePlanId}/events`;
  const response = await fetchOrThrow(url);
  return response.json();
};

export const addRoutePlanEvent = async (routePlanId, event) => {
  const { type, data, timestamp, ...rest } = event;
  const mapped = {
    ...rest,
    eventType: type ?? rest.eventType,
    eventData: data ?? rest.eventData,
  };
  const response = await fetchOrThrow(`/api/route-plans/${routePlanId}/events`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(mapped),
  });
  return response.json();
};

export const acknowledgeEvent = async (routePlanId, eventId) => {
  const response = await fetchOrThrow(`/api/route-plans/${routePlanId}/events/${eventId}/acknowledge`, {
    method: 'PUT',
  });
  return response.json();
};

// ============================================================================
// HELPERS - Funcoes auxiliares
// ============================================================================

export const saveCompletePlan = async (planData) => {
  const { plan, vehicles } = planData;

  let savedPlan;
  if (plan.id) {
    savedPlan = await updateRoutePlan(plan.id, plan);
  } else {
    savedPlan = await createRoutePlan(plan);
  }

  const savedVehicles = [];

  for (const vehicle of vehicles) {
    const vehicleData = {
      deviceId: vehicle.deviceId,
      driverId: vehicle.driverId,
      vehicleColor: vehicle.color,
      capacity: vehicle.capacity,
      startLatitude: vehicle.startLat,
      startLongitude: vehicle.startLng,
      routeGeometry: vehicle.routeGeometry,
      estimatedDistance: vehicle.distance,
      estimatedDuration: vehicle.duration,
    };

    const savedVehicle = await addRoutePlanVehicle(savedPlan.id, vehicleData);

    const savedStops = [];
    for (let i = 0; i < (vehicle.stops || []).length; i += 1) {
      const stop = vehicle.stops[i];
      const stopData = {
        vehicleId: savedVehicle.id,
        poiId: stop.poiId,
        name: stop.name || stop.address,
        address: stop.address,
        latitude: stop.lat,
        longitude: stop.lng,
        optimizedOrder: i + 1,
        serviceTime: stop.serviceTime || 300,
        priority: stop.priority || 'medium',
        estimatedArrival: stop.estimatedArrival,
        timeWindowStart: stop.timeWindowStart,
        timeWindowEnd: stop.timeWindowEnd,
        notes: stop.notes,
      };

      const savedStop = await addRoutePlanStop(savedPlan.id, stopData);
      savedStops.push(savedStop);
    }

    savedVehicles.push({ ...savedVehicle, stops: savedStops });
  }

  return { plan: savedPlan, vehicles: savedVehicles };
};

export const loadCompletePlan = async (planId) => {
  const summary = await getRoutePlanSummary(planId);
  return summary;
};

// Status constants (espelho do backend)
export const PLAN_STATUS = {
  DRAFT: 'draft',
  OPTIMIZED: 'optimized',
  APPROVED: 'approved',
  EXECUTING: 'executing',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
};

export const STOP_STATUS = {
  PENDING: 'pending',
  EN_ROUTE: 'en_route',
  ARRIVED: 'arrived',
  COMPLETED: 'completed',
  SKIPPED: 'skipped',
  FAILED: 'failed',
};

export const VEHICLE_STATUS = {
  PENDING: 'pending',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
};

export const EVENT_TYPES = {
  ROUTE_DEVIATION: 'route_deviation',
  LATE_ARRIVAL: 'late_arrival',
  EARLY_ARRIVAL: 'early_arrival',
  UNPLANNED_STOP: 'unplanned_stop',
  STOP_SKIPPED: 'stop_skipped',
  VEHICLE_BREAKDOWN: 'vehicle_breakdown',
  RE_ROUTING: 're_routing',
  STOP_COMPLETED: 'stop_completed',
  PLAN_STARTED: 'plan_started',
  PLAN_COMPLETED: 'plan_completed',
};

export const EVENT_SEVERITY = {
  INFO: 'info',
  WARNING: 'warning',
  ERROR: 'error',
  CRITICAL: 'critical',
};

export default {
  getRoutePlans,
  getRoutePlan,
  createRoutePlan,
  updateRoutePlan,
  deleteRoutePlan,
  getRoutePlanSummary,
  updateRoutePlanStatus,
  getRoutePlanStops,
  addRoutePlanStop,
  updateRoutePlanStop,
  removeRoutePlanStop,
  updateStopStatus,
  completeStop,
  skipStop,
  getRoutePlanVehicles,
  addRoutePlanVehicle,
  updateRoutePlanVehicle,
  removeRoutePlanVehicle,
  startVehicle,
  completeVehicle,
  getVehicleProgress,
  getRoutePlanEvents,
  addRoutePlanEvent,
  acknowledgeEvent,
  saveCompletePlan,
  loadCompletePlan,
  PLAN_STATUS,
  STOP_STATUS,
  VEHICLE_STATUS,
  EVENT_TYPES,
  EVENT_SEVERITY,
};
